import { getHttpClient } from '@/singleton-clients';
import { getHttpErrorStatus } from '@/utils/http-client';
import { devDebug } from '@/utils/dev-debug';

const CONSUMPTION_CAPTURE_PATH = 'api/v2/ai/llm/consumption/capture';
const CONSUMPTION_REPORT_PATH = 'api/v2/ai/llm/consumption/report';

export class QuotaExceededError extends Error {
	constructor(message = 'Quota exceeded') {
		super(message);
		this.name = 'QuotaExceededError';
	}
}

/**
 * Captures consumption of LLM tokens.
 * @param requestOptions - Optional request options (e.g. from withTrackingHeaders()) for user-action tracking headers.
 * @returns The capture UUID if successful, undefined otherwise.
 */
export async function captureConsumption(
	requestOptions?: RequestInit,
): Promise<string | undefined> {
	const client = getHttpClient();

	try {
		const response = await client.post<{ captureUuid?: string }>(
			CONSUMPTION_CAPTURE_PATH,
			{},
			requestOptions ?? {},
		);

		return response?.captureUuid;
	} catch (error: unknown) {
		const status = getHttpErrorStatus(error);

		if (status === 429) {
			throw new QuotaExceededError();
		}

		if (status === 400 || status === 422) {
			const message =
				(error as Error)?.message ??
				(status === 422
					? 'Request validation failed (e.g. missing x-feature-name or x-interface-name). Ensure tracking headers are passed.'
					: 'The LLM configuration is missing. Reach out to your Admin to configure your LLM provider.');
			devDebug('llmConsumption', { skippedReason: message });
			return undefined;
		}

		console.error('Error capturing consumption:', error);
		throw error;
	}
}

/**
 * Reports consumption of LLM tokens to the backend.
 * Fire-and-forget: failures are non-critical and are silently ignored.
 * @param quotaId - The capture UUID from captureConsumption.
 * @param requestOptions - Optional request options (e.g. from withTrackingHeaders()) for user-action tracking headers.
 */
export function reportConsumption(
	quotaId: string,
	requestOptions?: RequestInit,
): void {
	const client = getHttpClient();
	const url = `${CONSUMPTION_REPORT_PATH}?consumptionId=${encodeURIComponent(
		quotaId,
	)}`;
	client.post(url, {}, requestOptions ?? {}).catch(() => {
		/* Fire-and-forget: consumption report failures are non-critical */
	});
}
